// ─── src/chat/AuraLauncher.jsx ────────────────────────────────────────────────
// Desktop: invisible above fold → full pill fades in after 180px scroll.
// Mobile:  compact 42px circle only, no label, no auto-expand on scroll.
//          Stays docked at bottom-right with safe-area insets (iOS notch safe).
//
// ── Z-index budget (site-wide) ───────────────────────────────────────────────
//   99999  critical dialogs (confirm, conflict)
//    9999  modals / drawers
//    1002  AuraMiniBar (chat sheet / desktop modal)
//    1001  tooltips / popovers
//    1000  AuraLauncher FAB  ← raised from 900 to clear LWD homepage chat (999)
//     999  LWD homepage legacy chat bubble
//     900  page-level FABs (Edit Listing etc.)
//     850  GlobalCompare bar
//     100  CompareBar (older component, same page never shown together)
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useAura } from "../stores/auraCore";
import { useFloatingUi } from "../components/FloatingUiManager";
import { track } from "../utils/track";

const SCROLL_THRESHOLD = 180; // px before the label slides in (desktop only)

export default function AuraLauncher() {
  const _openMiniBar = useAura((s) => s.openMiniBar);
  const openMiniBar  = () => { track('aura_opened'); _openMiniBar(); };

  // Layout state comes from FloatingUiManager, no duplicate event listeners
  const { compareBarOpen, editorialMode, isMobile: mobile, auraBottomDesktop, auraBottomMobile } = useFloatingUi();

  const [hov,      setHov]      = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [scrolling, setScrolling] = useState(false);

  // Track scroll for desktop pill expand only
  useEffect(() => {
    const update = () => setExpanded(!mobile && window.scrollY > SCROLL_THRESHOLD);
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, [mobile]);

  // Mobile: glide the FAB out of the way while scrolling so it never sits on top
  // of content (buttons, prices, cards). It slides back ~650ms after scroll stops.
  useEffect(() => {
    if (!mobile) return;
    let t;
    const onScroll = () => { setScrolling(true); clearTimeout(t); t = setTimeout(() => setScrolling(false), 650); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); clearTimeout(t); };
  }, [mobile]);

  if (editorialMode) return null;
  // The partnership portal has its own vendor-facing "Ask Aura → see how couples
  // find you" launcher; the couple-facing chat orb would be a confusing duplicate.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/partnership")) return null;

  /* ── Mobile: compact 42px circle ──────────────────────────────────────────── */
  if (mobile) {
    return (
      <button
        onClick={openMiniBar}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        onTouchStart={() => setHov(true)}
        onTouchEnd={() => setHov(false)}
        aria-label="Open Aura chat assistant"
        style={{
          position:   "fixed",
          bottom:     auraBottomMobile,
          right:      "calc(max(16px, env(safe-area-inset-right, 0px) + 16px))",
          zIndex:     "var(--fui-z-aura-button)",
          width:      42,
          height:     42,
          borderRadius: "50%",
          background: hov ? "#b8922a" : "#d39a21",
          border:     "none",
          cursor:     "pointer",
          display:    "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow:  "0 4px 20px rgba(211,154,33,0.45)",
          transform:  scrolling ? "translateX(72px)" : (hov ? "scale(1.1)" : "scale(1)"),
          opacity:    scrolling ? 0 : 1,
          pointerEvents: scrolling ? "none" : "auto",
          transition: "background 0.2s, transform 0.3s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.25s ease, bottom 0.3s cubic-bezier(0.25,0.46,0.45,0.94)",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <span style={{ fontSize: 17, color: "#0f0d0a", lineHeight: 1, userSelect: "none" }}>
          ✦
        </span>
      </button>
    );
  }

  /* ── Desktop: collapsing pill ──────────────────────────────────────────────── */
  return (
    <button
      onClick={openMiniBar}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      aria-label="Open Aura chat assistant"
      style={{
        position:   "fixed",
        bottom:     auraBottomDesktop,
        right:      28,
        zIndex:     "var(--fui-z-aura-button)",
        transition: "bottom 0.3s cubic-bezier(0.25,0.46,0.45,0.94), background 0.2s ease, transform 0.2s ease, padding 0.5s cubic-bezier(0.25,0.46,0.45,0.94), gap 0.5s cubic-bezier(0.25,0.46,0.45,0.94), box-shadow 0.2s ease",
        display:    "flex",
        alignItems: "center",
        gap:        expanded ? 10 : 0,
        padding:    expanded ? "12px 22px 12px 16px" : "12px 16px",
        borderRadius: 100,
        background: hov ? "#b8922a" : "#d39a21",
        border:     "none",
        cursor:     "pointer",
        boxShadow:  "0 6px 28px rgba(211,154,33,0.4)",
        transform:  hov ? "translateY(-2px)" : "translateY(0)",
      }}
    >
      {/* Avatar / icon */}
      <span
        style={{
          width:          30,
          height:         30,
          borderRadius:   "50%",
          background:     "rgba(255,255,255,0.18)",
          display:        "flex",
          alignItems:     "center",
          justifyContent: "center",
          fontSize:       14,
          flexShrink:     0,
          color:          "#0f0d0a",
        }}
      >
        ✦
      </span>

      {/* Label, slides in on scroll */}
      <span
        aria-hidden={!expanded}
        style={{
          fontFamily:    "var(--font-body)",
          fontWeight:    600,
          fontSize:      13,
          color:         "#0f0d0a",
          letterSpacing: "0.4px",
          whiteSpace:    "nowrap",
          overflow:      "hidden",
          maxWidth:      expanded ? 160 : 0,
          opacity:       expanded ? 1   : 0,
          transition:    "max-width 0.5s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.35s ease",
        }}
      >
        Chat with Aura?
      </span>
    </button>
  );
}
